import { useLayoutEffect, useRef, type ReactNode } from "react";
import { FloatingSuggestionPanel } from "@/features/sparxsEditor/components/molecules/floatingSuggestionPanel/floatingSuggestionPanel";
import { focusChoiceControlAfterSync } from "./focusChoice";
import type { PickerListItem } from "@/features/sparxsEditor/components/molecules/pickerList/pickerList";
import { DirectiveBuilderStepPanels } from "../panels/stepPanels";
import {
  CHIP_NEXT_CHOICE_SURFACE,
  queryDirectiveBuilderChoiceInRoot,
  type DirectiveBuilderStep,
} from "../../suggestion/storage/session";
import type { DirectiveChoiceSlot } from "../../core/config";

export interface DirectiveBuilderFloatingProps {
  clientRect: (() => DOMRect | null) | null;
  step: DirectiveBuilderStep;

  items: PickerListItem[];
  selectedIndex: number;
  onSelectItem: (item: PickerListItem) => void;

  choiceSlot?: DirectiveChoiceSlot;
  choiceInitialValue?: string;
  onChoiceSubmit: (value: string) => void;

  stepTitle?: string;
  emptyMessage?: ReactNode;

  listFilterText?: string;
  onListFilterChange?: (value: string) => void;

  onListHighlightChange?: (index: number) => void;

  choiceStepKey?: number;

  onFloatingPanelRef?: (el: HTMLDivElement | null) => void;

  onFloatingChoiceFieldRef?: (el: HTMLElement | null) => void;
}

export const DirectiveBuilderFloating = ({
  clientRect,
  step,
  items,
  selectedIndex,
  onSelectItem,
  choiceSlot,
  choiceInitialValue = "",
  onChoiceSubmit,
  stepTitle,
  emptyMessage,
  listFilterText,
  onListFilterChange,
  onListHighlightChange,
  choiceStepKey = 0,
  onFloatingPanelRef,
  onFloatingChoiceFieldRef,
}: DirectiveBuilderFloatingProps) => {
  const floatingPanelRootRef = useRef<HTMLDivElement | null>(null);
  const floatingAllowedTriggerRef = useRef<HTMLButtonElement | null>(null);
  const setFloatingPanelRef = (node: HTMLDivElement | null) => {
    floatingPanelRootRef.current = node;
    onFloatingPanelRef?.(node);
  };
  // List steps: filter is auto-focused; cmdk Arrow keys call onListHighlightChange. Editor-focused
  // Arrow/Enter still route through handleDirectiveBuilderListKeyDown.

  useLayoutEffect(() => {
    if (step !== "choice" || choiceSlot == null) {
      onFloatingChoiceFieldRef?.(null);
      return;
    }
    const panel = floatingPanelRootRef.current;
    onFloatingChoiceFieldRef?.(queryDirectiveBuilderChoiceInRoot(panel));
  }, [step, choiceStepKey, choiceSlot, onFloatingChoiceFieldRef]);

  // SuggestedValues (cmdk): defer focus one frame. Freeform: when chip popover has no next field,
  // focus floating choice after sync (chip path uses scheduleChipNextChoiceFocus).
  useLayoutEffect(() => {
    if (step !== "choice" || choiceSlot == null) return;
    const panel = floatingPanelRootRef.current;
    const doc = panel?.ownerDocument ?? document;
    const queryChoiceEl = (): HTMLElement | null =>
      floatingAllowedTriggerRef.current ??
      queryDirectiveBuilderChoiceInRoot(panel);
    const needsDeferredFocus =
      choiceSlot.suggestedOptions != null &&
      choiceSlot.suggestedOptions.length > 0;
    if (needsDeferredFocus) {
      const id = requestAnimationFrame(() => {
        const el = queryChoiceEl();
        if (el != null) {
          el.focus({ preventScroll: true });
        }
      });
      return () => cancelAnimationFrame(id);
    }
    const isFreeform =
      choiceSlot.options == null && choiceSlot.suggestedOptions == null;
    if (!isFreeform) return;
    if (
      doc.querySelector(
        `[data-directive-session-surface="${CHIP_NEXT_CHOICE_SURFACE}"]`,
      ) != null
    ) {
      return;
    }
    const el = queryChoiceEl();
    let cancelFocus: (() => void) | undefined;
    if (el != null) {
      cancelFocus = focusChoiceControlAfterSync(el);
    }
    return () => cancelFocus?.();
    // choiceSlot is a new object every parent render; do not depend on it or we refocus the
    // trigger on unrelated updates and disrupt Radix Select (e.g. keyboard + Enter).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- choiceSlot identity is unstable; step + choiceStepKey are enough to refocus when the slot advances.
  }, [step, choiceStepKey]);

  return (
    <FloatingSuggestionPanel
      panelRef={setFloatingPanelRef}
      clientRect={clientRect}
      style={{ minWidth: 160 }}
    >
      <DirectiveBuilderStepPanels
        step={step}
        selectedIndex={selectedIndex}
        choiceStepKey={choiceStepKey}
        emptyMessage={emptyMessage}
        listFilterText={listFilterText}
        onListFilterChange={onListFilterChange}
        onListHighlightChange={onListHighlightChange}
        floatingAllowedTriggerRef={floatingAllowedTriggerRef}
        stepProps={{
          items,
          stepTitle,
          choiceSlot,
          choiceInitialValue,
          onSelectItem,
          onChoiceSubmit,
        }}
      />
    </FloatingSuggestionPanel>
  );
};
